import store from '@/store';
import Vue from 'vue';
import Router from 'vue-router';

import handleGetError from '@/utils/handleGetError';
import { captureFatalError } from '@/utils';

import { resetAppState, setPageData } from './middleware';
import routes from './routes';

Vue.use(Router);

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	scrollBehavior() {
		return typeof window.scroll === 'function'
			? window.scroll({ top: 0, left: 0, behavior: 'smooth' })
			: { x: 0, y: 0 };
	},
	routes
});

const shouldAllowGliaForRoute = route => {
	return !route || !route.meta || route.meta.allowGlia;
};

router.afterEach(async (to, from) => {
	if (this && typeof this.hasModule !== 'undefined' && this.hasModule('product') && to.meta.resetState) {
		store.commit('product/resetState');
		store.unregisterModule('product');
	}

	// Capture error page renders in Sentry with fatal severity
	if (to.meta.isErrorPage) {
		const actualError = store.state.error.error;
		const errorContext = {
			component: to.name || 'UnknownErrorPage',
			routeName: to.name,
			routePath: to.path,
			productType: store.getters['application/productType'],
			applicationToken: store.getters['application/applicationToken'],
			params: to.params,
			statusType: store.state.error.statusType,
			statusText: store.state.error.statusText,
			message: store.state.error.message
		};

		// Log to console for development visibility
		console.error('[ERROR PAGE]', {
			page: to.name || to.path,
			originalError: actualError,
			context: errorContext
		});

		// Capture the actual error (if available) with fatal severity
		if (actualError) {
			captureFatalError(
				actualError,
				{
					contexts: {
						errorPage: errorContext
					},
					tags: {
						errorType: 'error-page',
						errorComponent: errorContext.component,
						routeName: errorContext.routeName || 'unknown',
						productType: errorContext.productType || 'none',
						statusCode: String(errorContext.statusType)
					}
				}
			);
		} else {
			// Fallback if no error object available
			captureFatalError(
				new Error(`Error Page Rendered: ${errorContext.component}`),
				{
					contexts: {
						errorPage: errorContext
					},
					tags: {
						errorType: 'error-page',
						errorComponent: errorContext.component,
						routeName: errorContext.routeName || 'unknown',
						productType: errorContext.productType || 'none'
					}
				}
			);
		}
	}

	// Attach Glia integration script if feature toggle is on and script not already attached
	const allowGlia = shouldAllowGliaForRoute(to);
	const isGliaEnabled = store.state.vendor.isGliaToggleEnabled;

	if (allowGlia && isGliaEnabled) {
		if (!store.state.vendor.isGliaAttached) {
			await store.dispatch('vendor/attachGliaScript');
		}
	} else {
		await store.dispatch('vendor/detachGliaScript');
	}
});

router.beforeEach(async (incoming, last, next) => {
	try {
		await store.dispatch('branding/setBranding', {
			hostName: window.location.hostname,
			productName: 'OLA',
			themeCode: incoming.query.theme || null
		});
		await store.dispatch('personVerification/attachDeviceIdSDK');
		await store.dispatch('personVerification/attachDocumentVerificationSDK');
		await store.dispatch('personVerification/refreshDeviceIdSessionToken');

		// Load Glia toggle status once (will be cached for subsequent calls)
		await store.dispatch('vendor/loadGliaToggle');

		// let redirect = await enforceRoute(incoming);
		const redirect = await store.dispatch('routeEnforcer/enforceRoute', incoming);

		if (redirect) {
			return next();
		}

		if (incoming.meta.resetState) {
			resetAppState();
		}

		await setPageData(incoming);

		if (incoming.meta.bypassEnforcer && !incoming.meta.overrideTimeout) {
			store.commit('sessionTimer/resetTimers');
		} else if (!store.state.sessionTimer.sessionExpired) {
			await store.dispatch('sessionTimer/reset');
		}

		return next();
	} catch (error) {
		return handleGetError(error, incoming);
	}
});

export default router;
